import type { DiaryDocType } from '@ast-data/shared';
import { stripTemperatureUnit } from '../../utils/weatherTemperature';
import type { VisitorRow } from './visitorRows';
import type { SondagePhotoRow } from './sondagePhotoRows';
import { compareNatural } from '../../utils/compare';

/**
 * What the Tagebuch form holds while it is being edited, and the plain
 * conversions around it: a stored day into form values, an empty day, and the
 * couple of readings (work span, break) the form checks itself against.
 *
 * All of it is free of React and of the dialog, so the form itself is left with
 * the editing.
 */

export type FormValues = {
    author: string;
    excavationLeader: string;
    technician: string;
    staff: string;
    workStart: string;
    workEnd: string;
    breakTime: string;
    weatherCondition: string;
    weatherTemperature: string;
    weatherExtra: string;
    topsoilRemoval: string;
    surveying: string;
    cleaning: string;
    cuttingFindings: string;
    drawing: string;
    descriptions: string;
    recovery: string;
    finds: string;
    photos: string;
    excavatorType: string;
    excavatorCount: string;
    excavatorStart: string;
    excavatorEnd: string;
    excavatorBreak: string;
    excavatorNote: string;
    dailyCourse: string;
    lastFindingNumber: string;
    lastProfileNumber: string;
    lastStrayFindNumber: string;
    lastGeoprofileNumber: string;
    /** The last Sondage handed out — apart from the day's Sondage list. */
    lastSondageNumber: string;
    /**
     * The Teilfoto is NOT here: the Sondage/Teilfoto pairs are a list of their
     * own (`sondagePhotos`, held beside the form like the visitors), and the
     * day's last Teilfoto is derived from it on save.
     */
    recordedAt: string;
};

/** The break as a non-negative integer number of minutes. */
export function breakToMinutes(value: string): number {
    const num = Math.trunc(Number(value));
    return Number.isFinite(num) && num > 0 ? num : 0;
}

export function emptyValues(): FormValues {
    return {
        author: '',
        excavationLeader: '',
        technician: '',
        staff: '',
        workStart: '',
        workEnd: '',
        breakTime: '',
        weatherCondition: '',
        weatherTemperature: '',
        weatherExtra: '',
        topsoilRemoval: '',
        surveying: '',
        cleaning: '',
        cuttingFindings: '',
        drawing: '',
        descriptions: '',
        recovery: '',
        finds: '',
        photos: '',
        excavatorType: '',
        excavatorCount: '',
        excavatorStart: '',
        excavatorEnd: '',
        excavatorBreak: '',
        excavatorNote: '',
        dailyCourse: '',
        lastFindingNumber: '',
        lastProfileNumber: '',
        lastStrayFindNumber: '',
        lastGeoprofileNumber: '',
        lastSondageNumber: '',
        recordedAt: new Date().toISOString()
    };
}

export function toValues(diary: DiaryDocType): FormValues {
    return {
        author: diary.author,
        excavationLeader: diary.excavationLeader,
        technician: diary.technician,
        staff: diary.staff,
        workStart: diary.workStart,
        workEnd: diary.workEnd,
        breakTime: diary.breakTime,
        weatherCondition: diary.weatherCondition,
        // Records written before the field lost its unit still carry it; the
        // form shows the reading alone, so editing a day cleans it up.
        weatherTemperature: stripTemperatureUnit(diary.weatherTemperature),
        weatherExtra: diary.weatherExtra,
        topsoilRemoval: diary.topsoilRemoval,
        surveying: diary.surveying,
        cleaning: diary.cleaning,
        cuttingFindings: diary.cuttingFindings,
        drawing: diary.drawing,
        descriptions: diary.descriptions,
        recovery: diary.recovery,
        finds: diary.finds,
        photos: diary.photos,
        excavatorType: diary.excavatorType,
        excavatorCount: diary.excavatorCount,
        excavatorStart: diary.excavatorStart,
        excavatorEnd: diary.excavatorEnd,
        excavatorBreak: diary.excavatorBreak,
        excavatorNote: diary.excavatorNote,
        dailyCourse: diary.dailyCourse,
        lastFindingNumber: diary.lastFindingNumber,
        lastProfileNumber: diary.lastProfileNumber,
        lastStrayFindNumber: diary.lastStrayFindNumber,
        lastGeoprofileNumber: diary.lastGeoprofileNumber,
        lastSondageNumber: diary.lastSondageNumber,
        recordedAt: diary.recordedAt
    };
}

/** Distinct, sorted, non-empty values (autocomplete). */
export function distinct(values: string[]): string[] {
    return [...new Set(values.filter(Boolean))].sort(
        (firstValue, secondValue) => compareNatural(firstValue, secondValue)
    );
}

/** A stable key for the parcel-id / visitor / Sondage dirty comparison. */
export function embeddedKey(
    parcelIds: string[],
    visitors: VisitorRow[],
    sondagePhotos: SondagePhotoRow[]
): string {
    const parcels = [...parcelIds].sort().join(',');
    const people = visitors
        .map(
            (visitor) => `${visitor.name}\0${visitor.position}\0${visitor.note}`
        )
        .join('|');
    const sondagen = sondagePhotos
        .map((row) => `${row.sondage}\0${row.partialPhoto}`)
        .join('|');
    return `${parcels}::${people}::${sondagen}`;
}
